import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { Admin, LoginRequest, LoginResponse } from '@types'
import { authService } from '@services/authService'

interface AuthState {
  admin: Admin | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  loading: boolean
  error: string | null
}

const initialState: AuthState = {
  admin: null,
  token: localStorage.getItem('admin_token'),
  refreshToken: localStorage.getItem('admin_refresh_token'),
  isAuthenticated: false, // 初始状态保持false，让useAuth hook来验证
  loading: true, // 初始状态设为loading，等待验证
  error: null,
}

// 异步actions
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials)
      localStorage.setItem('admin_token', response.token)
      localStorage.setItem('admin_refresh_token', response.refreshToken)
      return response
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        '登录失败'
      return rejectWithValue(msg)
    }
  }
)

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout()
      localStorage.removeItem('admin_token')
      localStorage.removeItem('admin_refresh_token')
    } catch (error: any) {
      return rejectWithValue(error.message || '登出失败')
    }
  }
)

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState }
      const refreshToken = state.auth.refreshToken
      
      if (!refreshToken) {
        throw new Error('No refresh token available')
      }
      
      const response = await authService.refreshToken(refreshToken)
      localStorage.setItem('admin_token', response.token)
      return response
    } catch (error: any) {
      localStorage.removeItem('admin_token')
      localStorage.removeItem('admin_refresh_token')
      return rejectWithValue(error.message || 'Token刷新失败')
    }
  }
)

export const getCurrentAdmin = createAsyncThunk(
  'auth/getCurrentAdmin',
  async (_, { rejectWithValue }) => {
    try {
      const admin = await authService.getCurrentAdmin()
      return admin
    } catch (error: any) {
      return rejectWithValue(error.message || '获取管理员信息失败')
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload
      state.loading = false // 设置认证状态时也结束loading
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<LoginResponse>) => {
        state.loading = false
        state.admin = action.payload.admin
        state.token = action.payload.token
        state.refreshToken = action.payload.refreshToken
        state.isAuthenticated = true
        state.error = null
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
        state.isAuthenticated = false
      })
      
      // Logout
      .addCase(logout.pending, (state) => {
        state.loading = true
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false
        state.admin = null
        state.token = null
        state.refreshToken = null
        state.isAuthenticated = false
        state.error = null
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      
      // Refresh Token
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.token = action.payload.token
        state.isAuthenticated = true
      })
      .addCase(refreshToken.rejected, (state) => {
        state.admin = null
        state.token = null
        state.refreshToken = null
        state.isAuthenticated = false
      })
      
      // Get Current Admin
      .addCase(getCurrentAdmin.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getCurrentAdmin.fulfilled, (state, action) => {
        state.loading = false
        state.admin = action.payload
        state.isAuthenticated = true
        state.error = null
      })
      .addCase(getCurrentAdmin.rejected, (state, action) => {
        state.loading = false
        state.admin = null
        state.isAuthenticated = false
        state.error = action.payload as string
      })
  },
})

export const { clearError, setAuthenticated } = authSlice.actions
export default authSlice.reducer



